// Endpoint wrappers for the new REST auth surface.
//
// Every call returns the same ``RestEnvelope`` shape so the dashboard's
// view layer can keep using its existing ``mapServerError`` helper.  Side
// effects on token storage live here (login / setPassword / refresh) so
// callers don't have to remember to ``setTokens`` themselves.

import { callRest, refreshAccessToken, type RestEnvelope } from './restClient';
import { setTokens } from './storage';

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

export interface AuthUserPermission {
  code: string;
  name: string;
}

export interface AuthCountryDisplay {
  code: string;
  country: string;
}

export interface AuthStockSettings {
  allocationStrategy: string;
}

export interface AuthAccessibleChannel {
  id: string;
  slug: string;
  name: string;
  currencyCode: string;
  isActive: boolean;
  defaultCountry: AuthCountryDisplay;
  stockSettings: AuthStockSettings;
}

export interface AuthMetadataItem {
  key: string;
  value: string;
}

export interface AuthAvatar {
  url: string;
  alt?: string | null;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isStaff: boolean;
  isActive: boolean;
  dateJoined: string;
  metadata: AuthMetadataItem[];
  userPermissions: AuthUserPermission[];
  accessibleChannels: AuthAccessibleChannel[];
  restrictedAccessToChannels: boolean;
  avatar?: AuthAvatar | null;
}

export interface AuthTokenPayload {
  token: string;
  refreshToken?: string | null;
  csrfToken?: string | null;
  user: AuthUser;
}

export interface AuthMethodsPayload {
  passwordLoginMode: 'ENABLED' | 'DISABLED' | string;
  availableExternalAuthentications: Array<{ id: string; name: string }>;
}

export interface AuthVerifyPayload {
  isValid: boolean;
  payload: Record<string, unknown> | null;
  user: AuthUser | null;
}

export interface AuthRegisterPayload {
  user: AuthUser | null;
  requiresConfirmation: boolean;
}

export interface AuthChangePasswordPayload {
  user: AuthUser | null;
}

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export interface RegisterInput {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  redirectUrl?: string;
  channel?: string | null;
  languageCode?: string | null;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface PasswordResetInput {
  email: string;
  redirectUrl: string;
  channel?: string | null;
}

export interface PasswordSetInput {
  email: string;
  password: string;
  token: string;
}

export interface PasswordChangeInput {
  oldPassword: string;
  newPassword: string;
}

export interface ExternalInput {
  pluginId: string;
  input?: string;
}

// ---------------------------------------------------------------------------
// Endpoint paths (kept here so tests + restClient share one source of truth).
// ---------------------------------------------------------------------------

export const AUTH_PATHS = {
  register: '/api/auth/register/',
  login: '/api/auth/login/',
  logout: '/api/auth/logout/',
  refresh: '/api/auth/token/refresh/',
  verify: '/api/auth/token/verify/',
  passwordReset: '/api/auth/password/reset/',
  passwordSet: '/api/auth/password/set/',
  passwordChange: '/api/auth/password/change/',
  methods: '/api/auth/methods/',
  externalUrl: '/api/auth/external/url/',
  externalLogin: '/api/auth/external/login/',
  externalRefresh: '/api/auth/external/refresh/',
  externalVerify: '/api/auth/external/verify/',
  externalLogout: '/api/auth/external/logout/',
  me: '/api/me/',
} as const;

// ---------------------------------------------------------------------------
// Side-effecting wrappers
// ---------------------------------------------------------------------------

function persistTokens(envelope: RestEnvelope<AuthTokenPayload>): void {
  if (envelope.errors.length > 0 || !envelope.data) {
    return;
  }

  const { token, refreshToken } = envelope.data;

  setTokens({ accessToken: token, refreshToken: refreshToken ?? null });
}

// ---------------------------------------------------------------------------
// 14 endpoint functions
// ---------------------------------------------------------------------------

export function register(input: RegisterInput): Promise<RestEnvelope<AuthRegisterPayload>> {
  return callRest<AuthRegisterPayload>({
    method: 'POST',
    url: AUTH_PATHS.register,
    data: input,
  });
}

export async function login(input: LoginInput): Promise<RestEnvelope<AuthTokenPayload>> {
  const result = await callRest<AuthTokenPayload>({
    method: 'POST',
    url: AUTH_PATHS.login,
    data: input,
  });

  persistTokens(result);

  return result;
}

export function logout(refreshToken?: string | null): Promise<RestEnvelope<unknown>> {
  return callRest<unknown>({
    method: 'POST',
    url: AUTH_PATHS.logout,
    data: refreshToken ? { refreshToken } : {},
  });
}

/**
 * High-level wrapper around the single-flight refresh helper exposed by
 * ``restClient``.  Use this from views; the interceptor uses
 * ``refreshAccessToken`` directly to avoid envelope translation.
 */
export async function refreshToken(): Promise<RestEnvelope<{ token: string }>> {
  const next = await refreshAccessToken();

  if (!next) {
    return {
      data: null,
      errors: [{ field: 'refreshToken', code: 'JWT_INVALID_TOKEN', message: 'Refresh failed.' }],
      status: 401,
    };
  }

  return { data: { token: next }, errors: [], status: 200 };
}

export function verifyToken(token: string): Promise<RestEnvelope<AuthVerifyPayload>> {
  return callRest<AuthVerifyPayload>({
    method: 'POST',
    url: AUTH_PATHS.verify,
    data: { token },
  });
}

export function requestPasswordReset(input: PasswordResetInput): Promise<RestEnvelope<unknown>> {
  return callRest<unknown>({
    method: 'POST',
    url: AUTH_PATHS.passwordReset,
    data: input,
  });
}

export async function setPassword(
  input: PasswordSetInput,
): Promise<RestEnvelope<AuthTokenPayload>> {
  const result = await callRest<AuthTokenPayload>({
    method: 'POST',
    url: AUTH_PATHS.passwordSet,
    data: input,
  });

  persistTokens(result);

  return result;
}

export function changePassword(
  input: PasswordChangeInput,
): Promise<RestEnvelope<AuthChangePasswordPayload>> {
  return callRest<AuthChangePasswordPayload>({
    method: 'POST',
    url: AUTH_PATHS.passwordChange,
    data: input,
  });
}

export function getAuthMethods(): Promise<RestEnvelope<AuthMethodsPayload>> {
  return callRest<AuthMethodsPayload>({
    method: 'GET',
    url: AUTH_PATHS.methods,
  });
}

export function me(): Promise<RestEnvelope<AuthUser>> {
  return callRest<AuthUser>({
    method: 'GET',
    url: AUTH_PATHS.me,
  });
}

// External-auth stubs (always 404 until a real plugin lands).
export function externalAuthUrl(
  input: ExternalInput,
): Promise<RestEnvelope<{ authenticationData: string }>> {
  return callRest({ method: 'POST', url: AUTH_PATHS.externalUrl, data: input });
}

export function externalLogin(input: ExternalInput): Promise<RestEnvelope<AuthTokenPayload>> {
  return callRest({ method: 'POST', url: AUTH_PATHS.externalLogin, data: input });
}

export function externalRefresh(input: ExternalInput): Promise<RestEnvelope<AuthTokenPayload>> {
  return callRest({ method: 'POST', url: AUTH_PATHS.externalRefresh, data: input });
}

export function externalVerify(input: ExternalInput): Promise<RestEnvelope<AuthVerifyPayload>> {
  return callRest({ method: 'POST', url: AUTH_PATHS.externalVerify, data: input });
}

export function externalLogout(input: ExternalInput): Promise<RestEnvelope<unknown>> {
  return callRest({ method: 'POST', url: AUTH_PATHS.externalLogout, data: input });
}

export const authApi = {
  register,
  login,
  logout,
  refreshToken,
  verifyToken,
  requestPasswordReset,
  setPassword,
  changePassword,
  getAuthMethods,
  me,
  externalAuthUrl,
  externalLogin,
  externalRefresh,
  externalVerify,
  externalLogout,
};

export default authApi;
